import React, { memo } from 'react';
import { Box, Typography } from '@mui/material';
import { TripSummaryDto } from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';
import TripCard from './trip-card';

/**
 * Props for the TripDateSection component.
 */
export interface TripDateSectionProps {
	/** Date label to display as section header (e.g., "Today", "Yesterday", "15 January 2026"). */
	dateLabel: string;
	/** Array of trips that occurred on this date. */
	trips: TripSummaryDto[];
	/** Callback passed to each TripCard for viewing the trip / score breakdown. */
	onViewScoreBreakdown?: (tripId: string) => void;
	/** Whether cards show score badges and score wording. Default: false */
	showDriverScore?: boolean;
}

/**
 * Estimated card height used by the browser before a card has been laid out.
 * Keeps the scrollbar stable while offscreen cards are skipped.
 */
const ESTIMATED_CARD_HEIGHT_PX = 480;

/**
 * Groups trips under a date label with a divider header.
 *
 * Each card sits in a wrapper with `content-visibility: auto`, so the browser
 * skips layout and paint for cards that are far outside the viewport. Memoised
 * so appending a new page of trips does not re-render earlier sections.
 *
 * @component
 * @example
 * ```tsx
 * <TripDateSection
 *   dateLabel="Today"
 *   trips={todaysTrips}
 *   onViewScoreBreakdown={handleViewTrip}
 *   showDriverScore
 * />
 * ```
 */
const TripDateSection: React.FC<TripDateSectionProps> = memo(function TripDateSection({
	dateLabel,
	trips,
	onViewScoreBreakdown,
	showDriverScore = false,
}: TripDateSectionProps) {
	return (
		<Box component='section' sx={{ mb: 3 }}>
			<Typography
				variant='subtitle1'
				component='h2'
				fontWeight='bold'
				sx={{
					mb: 2,
					borderBottom: '1px solid',
					borderColor: 'divider',
					pb: 1,
				}}
			>
				{dateLabel}
			</Typography>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				{trips.map((trip) => (
					<Box
						key={trip.tripId}
						sx={{ contentVisibility: 'auto', containIntrinsicSize: `auto ${ESTIMATED_CARD_HEIGHT_PX}px` }}
					>
						<TripCard trip={trip} onViewScoreBreakdown={onViewScoreBreakdown} showDriverScore={showDriverScore} />
					</Box>
				))}
			</Box>
		</Box>
	);
});

export default TripDateSection;
